import {
  applyVerdict,
  type CartEntry,
  isOnCart,
  itemKey,
  type NewCartEntry,
  type ProductMemory,
  type Verdict,
} from "@larder/shared";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  type Unsubscribe,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Repository for the cart and for what the household thinks of a product.
 *
 * Two collections with deliberately different natures, which is why they share
 * a file — the contrast is the point:
 *
 *   `cart`          mutable intent. Add it, change your mind, delete it.
 *   `productMemory` a rollup of verdicts, rebuildable from `events`.
 *
 * Neither is the truth about what happened. That is still `events`, and a
 * verdict is written there first (ADR 0014).
 */

type Stored<T> = Omit<T, "id">;

const cartCol = (locId: string) => collection(db, "locations", locId, "cart");
const memoryDoc = (locId: string, key: string) =>
  doc(db, "locations", locId, "productMemory", key);
const eventsCol = (locId: string) => collection(db, "locations", locId, "events");

/**
 * Puts something on the list, unless it is already there.
 *
 * Deduped on the way in rather than at render time, because the list is shared:
 * two people who both notice the milk has run out should produce one line, not
 * two. Returns the id, or null when it was already on the list.
 */
export async function addToCart(
  locId: string,
  uid: string,
  input: NewCartEntry,
  existing: readonly CartEntry[],
): Promise<string | null> {
  const name = input.name.replace(/\s+/g, " ").trim();
  if (!name) return null;
  if (isOnCart(name, existing)) return null;

  const ref = doc(cartCol(locId));
  await setDoc(ref, {
    name,
    brand: input.brand ?? null,
    sizeText: input.sizeText ?? null,
    qty: input.qty ?? 1,
    key: itemKey(name),
    reason: input.reason,
    fromItemId: input.fromItemId ?? null,
    addedAt: Date.now(),
    addedByUid: uid,
  } satisfies Stored<CartEntry>);
  return ref.id;
}

/**
 * Off the list.
 *
 * Used both for "I changed my mind" and for "I bought it" — the difference is
 * recorded by the `added` event on the new item, not here. A cart with its own
 * history would be a second log to reconcile with the real one.
 */
export async function removeFromCart(locId: string, cartId: string): Promise<void> {
  await deleteDoc(doc(cartCol(locId), cartId));
}

export function watchCart(
  locId: string,
  onChange: (entries: CartEntry[]) => void,
  onError: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    // Newest first: the thing you just ran out of is the thing you are about to
    // forget.
    query(cartCol(locId), orderBy("addedAt", "desc")),
    (snap) => {
      onChange(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Stored<CartEntry>) })));
    },
    onError,
  );
}

/**
 * Records how something was, in both places it belongs.
 *
 * The `rated` event is the truth — timestamped, attributed, tied to the item,
 * and never rewritten. The `productMemory` document is a cache of it, written
 * in the same batch so the two cannot drift apart within a session.
 *
 * Read-then-write on the rollup is safe here in a way it would not be for a
 * spend counter: two people rating the same product in the same second is not a
 * scenario, and the worst case is a count that is one low against a log that is
 * still complete. The log is what a future rebuild reads.
 */
export async function recordVerdict(
  locId: string,
  uid: string,
  { itemId, name, verdict }: { itemId: string; name: string; verdict: Verdict },
): Promise<void> {
  const key = itemKey(name);
  if (!key) return;

  const now = Date.now();
  const previous = await readProductMemory(locId, key);
  const batch = writeBatch(db);

  batch.set(doc(eventsCol(locId)), {
    itemId,
    type: "rated",
    at: now,
    byUid: uid,
    source: "app",
    verdict,
  });
  batch.set(
    memoryDoc(locId, key),
    applyVerdict(previous, { key, label: name, verdict, at: now }),
  );

  await batch.commit();
}

/**
 * What we already know about this product, if anything.
 *
 * One document read on a known key, which is the whole reason the rollup
 * exists: this runs in the half-second after the user taps "Ate it", and a
 * prompt that arrives late is worse than no prompt (ADR 0014).
 */
export async function readProductMemory(
  locId: string,
  key: string,
): Promise<ProductMemory | null> {
  if (!key) return null;
  const snap = await getDoc(memoryDoc(locId, key));
  return snap.exists() ? (snap.data() as ProductMemory) : null;
}
